import moment from 'moment'

const SOURCE_RANK = {
  report: 3,
  dailyFile: 2,
  monthlyFile: 1,
}

const TIME_FORMATS = [
  'DD/MM/YYYY/HH:mm:ss',
  'DD/MM/YYYY HH:mm:ss',
  'DD/MM/YYYY/HH:mm',
  'DD/MM/YYYY HH:mm',
  'YYYY-MM-DD HH:mm:ss',
  'YYYY-MM-DDTHH:mm:ss',
  'DD-MM-YYYY HH:mm:ss',
  'DD MMM YYYY HH:mm:ss',
  'DD MMM YYYY HH:mm',
]

function roundRain (value) {
  const n = parseFloat(value)
  if (Number.isNaN(n)) return null
  return Math.round(n * 100) / 100
}

function parseTimeToUnix (value) {
  if (value == null || value === '') return null
  const raw = String(value).trim()
  if (/^\d+(\.\d+)?$/.test(raw)) {
    const n = Number(raw)
    if (!Number.isFinite(n) || n <= 0) return null
    return n > 1e12 ? Math.floor(n / 1000) : Math.floor(n)
  }
  for (let i = 0; i < TIME_FORMATS.length; i++) {
    const parsed = moment(raw, TIME_FORMATS[i], true)
    if (parsed.isValid()) return parsed.unix()
  }
  const fallback = moment(raw)
  return fallback.isValid() ? fallback.unix() : null
}

function dayKey (unixSec, offset) {
  return moment.unix(unixSec).utcOffset(offset).format('YYYY-MM-DD')
}

function splitCsvLine (line) {
  if (line.indexOf(';') >= 0 && line.indexOf(',') < 0) return line.split(';')
  return line.split(',')
}

function parseCsvRows (raw) {
  const text = String(raw || '').replace(/^\uFEFF/, '').trim()
  if (!text) return []
  const lines = text.split(/\r?\n/).filter((line) => line.trim())
  if (!lines.length) return []
  const headers = splitCsvLine(lines[0]).map((h) => h.trim().replace(/^"|"$/g, ''))
  const rows = []
  for (let i = 1; i < lines.length; i++) {
    const cols = splitCsvLine(lines[i])
    const row = {}
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = cols[j] == null ? '' : String(cols[j]).trim().replace(/^"|"$/g, '')
    }
    rows.push(row)
  }
  return rows
}

export function parseDailyLogCsv (raw) {
  const rows = parseCsvRows(raw)
  const points = []
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const timeVal = row.Time || row.time || row.TIMESTAMP || row.timestamp
    const rainVal = row.AccumulatedDaily || row.accumulatedDaily || row.rainday || row.RainDay || row.rain
    const ts = parseTimeToUnix(timeVal)
    const rain = roundRain(rainVal)
    if (ts == null || rain == null) continue
    points.push({ ts, rain })
  }
  return points
}

export function parseMonthlyLog (raw) {
  const text = String(raw || '').replace(/^\uFEFF/, '').trim()
  if (!text) return []
  const points = []
  const lines = text.split(/\r?\n/)
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    if (!line || !line.trim()) continue
    const tsMatch = line.match(/timestamp\s*=\s*([^,\s&]+)/i)
    const rainMatch = line.match(/rainday\s*=\s*([^,\s&]+)/i)
    if (tsMatch && rainMatch) {
      const ts = parseTimeToUnix(tsMatch[1])
      const rain = roundRain(rainMatch[1])
      if (ts != null && rain != null) points.push({ ts, rain })
    }
  }
  if (points.length) return points

  const stripped = text
    .split('kuota=').join('')
    .split('expire=').join('')
    .split('id=').join('')
    .split('rainday=').join('')
    .split('timestamp=').join('')
  const rows = parseCsvRows(`kuota,expire,id,timestamp,rainday\n${stripped}`)
  for (let i = 0; i < rows.length; i++) {
    const ts = parseTimeToUnix(rows[i].timestamp)
    const rain = roundRain(rows[i].rainday)
    if (ts != null && rain != null) points.push({ ts, rain })
  }
  return points
}

function setDay (map, unixSec, rain, source, offset) {
  if (unixSec == null || rain == null) return
  const key = dayKey(unixSec, offset)
  const current = map[key]
  const rank = SOURCE_RANK[source] || 0
  const currentRank = current ? (SOURCE_RANK[current.source] || 0) : 0
  if (!current || rank > currentRank) {
    map[key] = { rain, ts: unixSec, source }
    return
  }
  if (rank < currentRank) return
  if (source === 'dailyFile' && rain > current.rain) {
    map[key] = { rain, ts: unixSec, source }
    return
  }
  if (source !== 'dailyFile' && unixSec >= current.ts) {
    map[key] = { rain, ts: unixSec, source }
  }
}

function emptySnap () {
  return { docs: [], empty: true, forEach () {} }
}

async function safeGet (promise) {
  try {
    return await promise
  } catch (err) {
    console.warn('PCH chart query failed', err)
    return emptySnap()
  }
}

export async function resolveStationIds (db, stationValue) {
  if (!stationValue) return []
  const ids = new Set([stationValue])
  let snap = await safeGet(db.collection('device').doc(stationValue).get())
  if (!snap.exists) {
    const byMac = await safeGet(db.collection('device').where('macAddress', '==', stationValue).limit(1).get())
    if (!byMac.empty) snap = byMac.docs[0]
  }
  if (snap && snap.exists) {
    ids.add(snap.id)
    const data = snap.data() || {}
    if (data.newID) ids.add(data.newID)
    if (data.macAddress) ids.add(data.macAddress)
  }
  return Array.from(ids).filter(Boolean)
}

async function queryAcrossIds (ids, buildQuery) {
  const snaps = await Promise.all(ids.map((id) => safeGet(buildQuery(id))))
  const docs = []
  snaps.forEach((snap) => {
    if (!snap || !snap.docs) return
    snap.docs.forEach((doc) => docs.push(doc))
  })
  return docs
}

function applyReportDocs (map, docs, offset, startSec, endSec) {
  docs.forEach((doc) => {
    const data = doc.data() || {}
    const ts = parseTimeToUnix(data.time)
    const rain = roundRain(data.rain)
    if (ts == null || rain == null) return
    if (startSec != null && ts < startSec) return
    if (endSec != null && ts > endSec) return
    setDay(map, ts, rain, 'report', offset)
  })
}

function applyFilePoints (map, points, source, offset, startSec, endSec) {
  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    if (startSec != null && point.ts < startSec) continue
    if (endSec != null && point.ts > endSec) continue
    setDay(map, point.ts, point.rain, source, offset)
  }
}

function hasMissingKeys (map, expectedKeys) {
  if (!expectedKeys || !expectedKeys.length) return true
  for (let i = 0; i < expectedKeys.length; i++) {
    if (!map[expectedKeys[i]]) return true
  }
  return false
}

async function fetchMergedDayMap (db, ids, startSec, endSec, offset, options = {}) {
  const map = {}
  if (!options.filesOnly) {
    const reportDocs = await queryAcrossIds(ids, (id) =>
      db.collection('report').doc(id).collection('rain')
        .orderBy('time', 'asc')
        .where('time', '>=', startSec)
        .where('time', '<=', endSec)
        .get()
    )
    applyReportDocs(map, reportDocs, offset, startSec, endSec)
    if (!hasMissingKeys(map, options.expectedKeys)) return map
  }

  const startMs = (options.fileStartSec != null ? options.fileStartSec : startSec) * 1000
  const endMs = (options.fileEndSec != null ? options.fileEndSec : endSec) * 1000
  const needDailyFiles = options.includeDailyFiles !== false
  const needMonthlyFiles = options.includeMonthlyFiles !== false
  const fileJobs = []
  if (needDailyFiles) {
    fileJobs.push(queryAcrossIds(ids, (id) =>
      db.collection('files').doc(id).collection('data')
        .orderBy('time', 'asc')
        .where('time', '>=', startMs)
        .where('time', '<=', endMs)
        .get()
    ).then((docs) => {
      docs.forEach((doc) => {
        applyFilePoints(map, parseDailyLogCsv(doc.data().data), 'dailyFile', offset, startSec, endSec)
      })
    }))
  }
  if (needMonthlyFiles) {
    fileJobs.push(queryAcrossIds(ids, (id) =>
      db.collection('files').doc(id).collection('monthly')
        .orderBy('time', 'asc')
        .where('time', '>=', startMs)
        .where('time', '<=', endMs + (40 * 86400000))
        .get()
    ).then((docs) => {
      docs.forEach((doc) => {
        applyFilePoints(map, parseMonthlyLog(doc.data().data), 'monthlyFile', offset, startSec, endSec)
      })
    }))
  }
  await Promise.all(fileJobs)
  return map
}

function newestFirst (labels, datasets) {
  return {
    labels: labels.slice().reverse(),
    datasets: datasets.slice().reverse(),
  }
}

function visibleDayKeys (year, monthNum, offset) {
  const days = moment({ year, month: monthNum, day: 1 }).daysInMonth()
  const today = moment().utcOffset(offset).format('YYYY-MM-DD')
  const keys = []
  for (let d = 1; d <= days; d++) {
    const key = `${year}-${String(monthNum + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    if (key > today) break
    keys.push(key)
  }
  return keys
}

async function loadDaily (db, ids, year, monthNum, offset) {
  const start = moment({ year, month: monthNum, day: 1 }).startOf('day')
  const end = start.clone().endOf('month')
  const startSec = Math.floor(start.valueOf() / 1000)
  const endSec = Math.floor(end.valueOf() / 1000)
  const keys = visibleDayKeys(year, monthNum, offset)
  const map = await fetchMergedDayMap(db, ids, startSec, endSec, offset, {
    expectedKeys: keys,
    fileStartSec: startSec - 86400,
    fileEndSec: endSec + 2 * 86400,
  })
  const series = newestFirst(
    keys.map((key) => moment(key, 'YYYY-MM-DD').format('DD MMM')),
    keys.map((key) => (map[key] ? map[key].rain : null))
  )
  return {
    labels: series.labels,
    datasets: series.datasets,
  }
}

async function fetchReportMonthly (db, ids, year) {
  const docs = await queryAcrossIds(ids, (id) =>
    db.collection('reportMonthly').where('id', '==', id).where('year', '==', year).orderBy('month', 'desc').get()
  )
  const months = {}
  docs.forEach((doc) => {
    const data = doc.data() || {}
    const month = parseInt(data.month, 10)
    const rain = roundRain(data.rain)
    if (Number.isNaN(month) || rain == null) return
    if (months[month] == null) months[month] = rain
  })
  return months
}

async function fetchReportYearly (db, ids) {
  const docs = await queryAcrossIds(ids, (id) =>
    db.collection('reportYearly').where('id', '==', id).get()
  )
  const years = {}
  docs.forEach((doc) => {
    const data = doc.data() || {}
    const year = parseInt(data.year, 10)
    const rain = roundRain(data.rain)
    if (Number.isNaN(year) || rain == null) return
    if (years[year] == null) years[year] = rain
  })
  return years
}

function sumDaysByMonth (dayMap) {
  const sums = {}
  const hasDays = {}
  Object.keys(dayMap).forEach((key) => {
    const month = moment(key, 'YYYY-MM-DD').month()
    const rain = dayMap[key].rain
    sums[month] = roundRain((sums[month] || 0) + rain)
    hasDays[month] = true
  })
  return { sums, hasDays }
}

function sumDaysByYear (dayMap) {
  const sums = {}
  Object.keys(dayMap).forEach((key) => {
    const year = moment(key, 'YYYY-MM-DD').year()
    const rain = dayMap[key].rain
    sums[year] = roundRain((sums[year] || 0) + rain)
  })
  return sums
}

async function loadMonthly (db, ids, year, offset) {
  const start = moment({ year, month: 0, day: 1 }).startOf('day')
  const end = moment({ year, month: 11, day: 31 }).endOf('day')
  const startSec = Math.floor(start.valueOf() / 1000)
  const endSec = Math.floor(end.valueOf() / 1000)
  const now = moment().utcOffset(offset)
  const expectedKeys = []
  for (let month = 0; month < 12; month++) {
    if (year > now.year() || (year === now.year() && month > now.month())) break
    expectedKeys.push.apply(expectedKeys, visibleDayKeys(year, month, offset))
  }
  const [dayMap, reportMonths] = await Promise.all([
    fetchMergedDayMap(db, ids, startSec, endSec, offset, {
      expectedKeys,
      fileStartSec: startSec - 5 * 86400,
      fileEndSec: endSec + 2 * 86400,
    }),
    fetchReportMonthly(db, ids, year),
  ])
  const { sums, hasDays } = sumDaysByMonth(dayMap)
  const labels = []
  const datasets = []
  for (let month = 0; month < 12; month++) {
    if (year > now.year() || (year === now.year() && month > now.month())) break
    labels.push(moment({ month }).format('MMMM'))
    if (hasDays[month]) {
      datasets.push(sums[month])
    } else if (reportMonths[month] != null) {
      datasets.push(reportMonths[month])
    } else {
      datasets.push(null)
    }
  }
  const series = newestFirst(labels, datasets)
  return {
    labels: series.labels,
    datasets: series.datasets,
  }
}

async function loadYearly (db, ids, offset) {
  const reportYears = await fetchReportYearly(db, ids)
  const monthlyDocs = await queryAcrossIds(ids, (id) =>
    db.collection('reportMonthly').where('id', '==', id).get()
  )
  const monthlyByYear = {}
  monthlyDocs.forEach((doc) => {
    const data = doc.data() || {}
    const year = parseInt(data.year, 10)
    const rain = roundRain(data.rain)
    if (Number.isNaN(year) || rain == null) return
    monthlyByYear[year] = roundRain((monthlyByYear[year] || 0) + rain)
  })

  const years = Array.from(new Set([
    ...Object.keys(reportYears).map(Number),
    ...Object.keys(monthlyByYear).map(Number),
  ])).filter((year) => !Number.isNaN(year)).sort((a, b) => a - b)

  let fileYearSums = {}
  const missingYears = years.filter((year) => reportYears[year] == null)
  if (missingYears.length) {
    const startYear = Math.min.apply(null, missingYears)
    const endYear = Math.max.apply(null, missingYears)
    const start = moment({ year: startYear, month: 0, day: 1 }).startOf('day')
    const end = moment({ year: endYear, month: 11, day: 31 }).endOf('day')
    const dayMap = await fetchMergedDayMap(db, ids, Math.floor(start.valueOf() / 1000), Math.floor(end.valueOf() / 1000), offset, {
      filesOnly: true,
      includeDailyFiles: false,
      fileStartSec: Math.floor(start.valueOf() / 1000) - 5 * 86400,
      fileEndSec: Math.floor(end.valueOf() / 1000) + 2 * 86400,
    })
    fileYearSums = sumDaysByYear(dayMap)
    Object.keys(fileYearSums).forEach((yearKey) => {
      const year = parseInt(yearKey, 10)
      if (years.indexOf(year) < 0) years.push(year)
    })
    years.sort((a, b) => a - b)
  }

  const labels = []
  const datasets = []
  years.forEach((year) => {
    labels.push(String(year))
    if (reportYears[year] != null) {
      datasets.push(reportYears[year])
    } else if (fileYearSums[year] != null) {
      datasets.push(fileYearSums[year])
    } else if (monthlyByYear[year] != null) {
      datasets.push(monthlyByYear[year])
    } else {
      datasets.push(null)
    }
  })
  const series = newestFirst(labels, datasets)
  return {
    labels: series.labels,
    datasets: series.datasets,
  }
}

export async function loadPchChart (db, { station, tab, year, monthNum, offset }) {
  const empty = { labels: [], datasets: [] }
  if (station == null || tab == null) return empty
  const ids = await resolveStationIds(db, station)
  if (!ids.length) return empty
  const tz = offset == null ? 7 : offset
  if (tab === 0) return loadDaily(db, ids, year, monthNum, tz)
  if (tab === 1) return loadMonthly(db, ids, year, tz)
  if (tab === 2) return loadYearly(db, ids, tz)
  return empty
}
